import React, { useContext } from 'react';
import style from './memberDocs.scss';
import { APPEARANCES, Heading } from '../../PageElements/Heading';
import { sectionPrefix } from '../../NavigationMenu/utils';
import { pathToLink } from '../../../../utils/locationUtils';
import { ReactMarkdownDisplay } from '../../PageElements/ReactMarkdownDisplay';
import { Text } from '../../PageElements/Text';
import { Note } from '../Note';
import { MetaDataRectangle } from './MetaDataRectangle';
import { MemberLabels } from './MemberLabels';
import { getName } from '../../../../utils/treeVisitors/helpers';
import { MemberActionItems } from './MemberActionItems';
import { getOperationMenuName } from '../../../../utils';
import { StoreContext } from '../../../appContext/StoreProvider';

export const MemberTypes = {
  MAIN: 'MAIN',
  EVENT: 'EVENT',
  PROPERTY: 'PROPERTY',
  API_REFERENCE_CATEGORY: 'API_REFERENCE_CATEGORY',
  OPERATION: 'OPERATION',
};

interface MemberObject {
  urlFormat?: string;
  topic?: string;
  notes?: any;
  docs?: any;
  extra?: any;
  deprecated?: boolean;
  description?: string;
  customLabels?: any;
  serviceName?: string;
}

interface MemberDocsProps {
  memberType?: string;
  memberObject?: MemberObject;
  node?: any;
  isMainCategory?: boolean;
  prefix?: string;
  firstGuideLink?: string;
}

export default function MemberDocs({
  memberType,
  memberObject,
  node = {},
  isMainCategory,
  prefix,
  firstGuideLink,
}: MemberDocsProps) {
  const { store } = useContext(StoreContext);
  const JSLayoutType = node?.data?.layoutType === 'JS';
  const {
    urlFormat,
    topic,
    notes = [],
    docs = {},
    extra: memberExtra = {},
    deprecated,
    description,
    customLabels,
    serviceName,
  } = memberObject;

  const nodeExtra = node?.data?.extra || {};
  const relevantCustomLabels = memberExtra.customLabels || customLabels;
  const artifactId =
    memberType !== MemberTypes.API_REFERENCE_CATEGORY
      ? memberExtra?.artifactId || nodeExtra?.artifactId
      : undefined;

  const name =
    memberType === MemberTypes.OPERATION
      ? getOperationMenuName(
          getName(memberObject),
          store.viewer.viewerConfig.layoutType,
          JSLayoutType,
        )
      : getName(memberObject);

  return (
    <>
      <div className={style.memberHeaderRow}>
        <Heading
          dataHook="member-docs-title"
          appearance={APPEARANCES.H1}
          data-tip={name}
          className={style.title}
          isCategoryTitle={isMainCategory}
        >
          {name}
        </Heading>
        <MemberActionItems memberSrc={docs.src} artifactId={artifactId} />
      </div>
      <MemberLabels
        deprecated={deprecated}
        customLabels={relevantCustomLabels}
      />
      {hasSummary() && (
        <div className={style.summary}>
          <ReactMarkdownDisplay
            markdown={getSummaryMarkdown()}
            prefix={prefix}
            dataHooks={{
              a: 'member-docs-summary-link',
              p: 'member-docs-summary-p',
            }}
            mdName={urlFormat}
          />
        </div>
      )}
      <MetaDataRectangle
        artifactId={artifactId}
        serviceName={serviceName}
        topic={topic}
      />
      {hasDescription() && (
        <div key={`${name}.description-body`} className={style.description}>
          <Text weight="bold">Description</Text>
          <ReactMarkdownDisplay
            prefix={prefix}
            dataHooks={{ p: 'member-docs-description' }}
            markdown={docs.description}
            mdName={urlFormat}
          />
        </div>
      )}
      {hasNotes() && (
        <Note sideLineColor="blue" title="Note:">
          <ReactMarkdownDisplay
            markdown={notes.join('\r\n')}
            prefix={prefix}
            mdName={urlFormat}
          />
        </Note>
      )}
    </>
  );

  function hasSummary() {
    if (memberType === MemberTypes.EVENT) {
      return !!docs.summary || !!description;
    }
    return !!docs.summary;
  }

  function getSummaryMarkdown() {
    if (memberType === MemberTypes.EVENT) {
      return docs.summary || description;
    }

    return docs.summary + (firstGuideLink ? getLearnMoreLink() : '');
  }

  function hasDescription() {
    return docs?.description?.length > 0;
  }

  function hasNotes() {
    return notes?.length > 0;
  }

  function getLearnMoreLink() {
    const _prefix = sectionPrefix(prefix, firstGuideLink);
    const learnMoreGuideLink = pathToLink(_prefix);
    return ` [Learn more](${learnMoreGuideLink}).`;
  }
}
